import { find, propEq, values, toLower, length, pipe, keys, head, path, prop } from 'ramda'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Box } from '@/components/structure'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import { show as showPaymentDetailsModal } from '@/features/Payments/payment-details-modal'
import { confirm } from '@/components/dialog'
import { Label, Row } from '@/components/form'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import Panel from '@/components/panel'
import AreaTitle from '@/components/area-title'
import useBasic from '@/hooks/use-basic'
import { verifyPayoutDeletion } from '@/models/settlements/ports'
import { breakpoints, colors, fontWeights } from '@/styles'
import { isNullOrEmpty } from '@/utils'
import { getFormattedCountries } from '@/utils/address'
import useDirectDeposit from '@/hooks/use-direct-deposit'
import Icon from '@/components/icon'
import { TrashIcon } from '@/components/icons'

const StyledPage = styled(Page)`
  height: 100%;
`

const Wrapper = styled.div`
  width: 100%;
  margin: auto;
  padding: 0 16px 100px;
  flex: 1;
`

const ContentWrapper = styled.div`
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
`

const StyledPanel = styled(Panel)`
  text-align: left;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0px;
  }
`

const StyledRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
  margin-left: 0;
  margin-right: 0;
  border-top: ${props => (props.borderTop ? 1 : 0)}px solid ${colors.grey};
`

const StyledLabel = styled(Label)`
  flex: 1;
  margin-right: 20px;
  font-weight: ${fontWeights.medium};
`

const StyledValue = styled(Label)`
  flex: 1.5;
  text-align: right;
  overflow-x: hidden;
  text-overflow: ellipsis;
`

const ViewDirectDeposit = ({ history }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { countries, financial_institutions } = useBasic(['countries', 'financial_institutions'])
  const formattedCountries = getFormattedCountries(countries)
  const [deleteAllowed, setDeleteAllowed] = useState(false)

  const { data } = useDirectDeposit()

  useEffect(() => {
    const verifyDelete = async () => {
      const { response, body } = await verifyPayoutDeletion()
      if (response.ok) {
        setDeleteAllowed(body)
      }
    }
    verifyDelete()
  }, [])

  const isLoading = isNullOrEmpty(data)

  const accountTypes = [
    {
      value: 'checking',
      label: t('l.payment_settings.edit.checking_account.label', 'Chequing account')
    },
    {
      value: 'savings',
      label: t('l.payment_settings.edit.savings_account.label', 'Savings account')
    }
  ]

  const handleModalClose = async () => {
    const response = await dispatch.settlements.deleteUnionPayPayout()
    if (response.ok) {
      history.push('/settings/payment-settings')
    }
  }

  const handleRedirect = deleteAllowed => {
    const listingId = pipe(keys, head)(deleteAllowed.listings)
    const payment = pipe(path([listingId]), head)(deleteAllowed.listings)
    const paymentId = prop('payment_request_id', payment)
    history.push(`/tenant/${listingId}/payments`)
    showPaymentDetailsModal({
      paymentId,
      listingId
    })
  }

  const defaultModalTitle = t(
    'l.payment_settings.edit.remove_payout_deposit.title',
    'Remove Bank Account for Automated Rent & Bitcoin Payments?'
  )

  const errorModalTitle = t(
    'l.payment_settings.edit.delete_pad_error.title',
    'Bank Account Cannot Be Removed'
  )

  const defaultModalBody = t(
    'l.payment_settings.edit.delete_payment_account.text',
    'Are you sure you want to continue? You will no longer be able to receive automated rent nor Bitcoin payments until a new account is connected.'
  )

  const defaultModalOptions = {
    buttonContainerStyle: {
      gap: '24px',
      flexDirection: 'row-reverse'
    },
    okLabel: (
      <>
        <Icon id="trash" width={12} height={12} />
        {t('common.remove', 'Remove')}
      </>
    ),
    okButtonProps: {
      theme: 'delete',
      minWidth: '204px'
    },
    cancelLabel: t('common.back', 'Back'),
    cancelButtonProps: {
      theme: 'secondary',
      minWidth: '204px',
      maxHeight: '40px'
    },
    width: '496px',
    padding: '32px'
  }

  const errorModalOptions = {
    buttonContainerStyle: {
      gap: '24px'
    },
    cancelLabel: t('common.back', 'Back'),
    cancelButtonProps: {
      theme: 'secondary',
      minWidth: '204px',
      maxHeight: '40px'
    },
    okLabel: t('r.listing.create.go_to_payment.button', 'Go to Payment'),
    okButtonProps: {
      theme: 'secondary',
      minWidth: '204px'
    },
    width: '496px',
    padding: '32px'
  }

  const showModal = () => {
    const isError = !deleteAllowed.is_allowed
    const count = length(Object.keys(deleteAllowed.listings))

    return isError
      ? confirm(
          errorModalTitle,
          <Trans i18nKey="l.payment_settings.edit.delete_account_error.body" count={count}>
            You currently have {{ count }} listing(s) with upcoming payments scheduled. Please stop
            those payments before unlinking your banking information.
          </Trans>,
          errorModalOptions
        ).then(() => handleRedirect(deleteAllowed))
      : confirm(defaultModalTitle, defaultModalBody, defaultModalOptions).then(handleModalClose)
  }

  const getCountryName = countryCode =>
    find(propEq('value', toLower(countryCode)), formattedCountries)?.label

  const formatAddress = ({ street1, street2, city, state, country }) => {
    const fullCountry = getCountryName(country)
    return `${street1}, ${street2 ? street2 + ',' : ''} ${city} ${state}, ${fullCountry}`
  }

  const getBankName = bank =>
    find(propEq('txt_id', bank))(values(financial_institutions))?.name || bank

  const getAccountType = account => find(propEq('value', account))(accountTypes)?.label

  return (
    <StyledPage>
      <Wrapper>
        <Helmet>
          <title>{t('l.payment_settings.view.direct_deposit.title', 'Direct Deposit')}</title>
        </Helmet>
        <PageTitle>{t('l.payment_settings.view.direct_deposit.title', 'Direct Deposit')}</PageTitle>
        {!isLoading && (
          <ContentWrapper>
            <AreaTitle>
              {t('b.payment_settings.edit.general_information.text', 'General Information')}
            </AreaTitle>
            <StyledPanel hSpace={32} vSpace={32}>
              <StyledRow bottom={10}>
                <StyledLabel>{t('l.payment_settings.view.name.label', 'Name')}</StyledLabel>
                <StyledValue>{`${data.personal_info.first_name} ${data.personal_info.last_name}`}</StyledValue>
              </StyledRow>
              {data.personal_info.company_name && (
                <StyledRow bottom={10}>
                  <StyledLabel>{t('l.payment_settings.view.company.label', 'Company')}</StyledLabel>
                  <StyledValue>{data.personal_info.company_name}</StyledValue>
                </StyledRow>
              )}
              <StyledRow bottom={10}>
                <StyledLabel>{t('l.payment_settings.view.address.label', 'Address')}</StyledLabel>
                <StyledValue>{formatAddress(data.personal_info)}</StyledValue>
              </StyledRow>
              <StyledRow>
                <StyledLabel>
                  {t('l.payment_settings.view.postal_code.label', 'Postal code')}
                </StyledLabel>
                <StyledValue>{data.personal_info.zip.toUpperCase()}</StyledValue>
              </StyledRow>
            </StyledPanel>
            <AreaTitle>
              {t(
                'l.payment_settings.view.financial_account_information.subtitle',
                'Financial Account Information'
              )}
            </AreaTitle>
            <StyledPanel hSpace={32} vSpace={32}>
              <StyledRow bottom={10}>
                <StyledLabel>
                  {t(
                    'l.payment_settings.view.financial_institution.label',
                    'Financial institution'
                  )}
                </StyledLabel>
                <StyledValue>
                  {getBankName(data.financial_info.financial_institution_name)}
                </StyledValue>
              </StyledRow>
              <StyledRow bottom={10}>
                <StyledLabel>
                  {t('l.payment_settings.view.account_type.label', 'Account type')}
                </StyledLabel>
                <StyledValue>{getAccountType(data.financial_info.account_type)}</StyledValue>
              </StyledRow>
              <StyledRow>
                <StyledLabel>
                  {t('l.payment_settings.view.account_number.label', 'Account number')}
                </StyledLabel>
                <StyledValue>{data.financial_info.account_number}</StyledValue>
              </StyledRow>
            </StyledPanel>
          </ContentWrapper>
        )}
      </Wrapper>
      <DefaultButtonBar
        handleBack={() => history.push('/settings/payment-settings/direct-deposit')}
      >
        <Button theme="delete" size="small" onClick={showModal}>
          <Box pleft="2px" pright="2px" as="span">
            <TrashIcon />
          </Box>
          {t('common.remove', 'Remove')}
        </Button>
      </DefaultButtonBar>
    </StyledPage>
  )
}

export default ViewDirectDeposit
